import React, { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { Mail, MessageCircleHeart, Sparkles } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { fadeUp, revealProps, stagger } from "@/lib/motion";

const STEPS = [
  {
    icon: Mail,
    title: "Оставьте почту",
    desc: "Пришлём код, сказки сохранятся на вашей полке",
    testId: LANDING.stepOne,
  },
  {
    icon: MessageCircleHeart,
    title: "Расскажите про ребёнка",
    desc: "Имя, возраст и что сейчас волнует",
    testId: LANDING.stepTwo,
  },
  {
    icon: Sparkles,
    title: "Через пару минут сказка готова",
    desc: "Текст, озвучка, картинка и видео",
    testId: LANDING.stepThree,
  },
];

const HowItWorks = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 0.85", "end 0.55"],
  });
  const raw = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const pathLength = useSpring(raw, { stiffness: 60, damping: 24, restDelta: 0.001 });

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="relative overflow-hidden bg-night-800 py-16 md:py-24"
    >
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div {...revealProps} variants={stagger(0.08)} className="mb-12 text-center">
          <motion.p variants={fadeUp} className="mb-3 text-[11px] uppercase tracking-[0.22em] text-amber">
            Три шага
          </motion.p>
          <motion.h2 variants={fadeUp} className="mb-4 font-serif text-3xl text-cream sm:text-4xl lg:text-5xl">
            Как получить сказку
          </motion.h2>
          <motion.p variants={fadeUp} className="mx-auto max-w-xl leading-relaxed text-muted-foreground">
            От почты до готовой сказки — пара минут, без установки приложений
          </motion.p>
        </motion.div>

        <div className="relative">
          {/* Путь между шагами дорисовывается по мере прокрутки */}
          <svg
            aria-hidden="true"
            viewBox="0 0 1000 40"
            preserveAspectRatio="none"
            className="pointer-events-none absolute inset-x-0 top-7 hidden h-10 w-full md:block"
          >
            <path
              d="M 167 20 C 300 -12, 380 48, 500 20 S 720 -6, 833 20"
              fill="none"
              stroke="rgba(185,182,211,0.16)"
              strokeWidth="2"
              strokeDasharray="6 8"
            />
            <motion.path
              d="M 167 20 C 300 -12, 380 48, 500 20 S 720 -6, 833 20"
              fill="none"
              stroke="#f5b74a"
              strokeWidth="2.5"
              strokeLinecap="round"
              style={{ pathLength }}
            />
          </svg>

          <svg
            aria-hidden="true"
            viewBox="0 0 40 1000"
            preserveAspectRatio="none"
            className="pointer-events-none absolute bottom-16 left-[2.1rem] top-16 w-10 md:hidden"
          >
            <path
              d="M 20 0 L 20 1000"
              fill="none"
              stroke="rgba(185,182,211,0.16)"
              strokeWidth="2"
              strokeDasharray="6 8"
            />
            <motion.path
              d="M 20 0 L 20 1000"
              fill="none"
              stroke="#f5b74a"
              strokeWidth="2.5"
              strokeLinecap="round"
              style={{ pathLength }}
            />
          </svg>

          <motion.ol
            {...revealProps}
            variants={stagger(0.12)}
            className="relative grid gap-5 md:grid-cols-3"
          >
            {STEPS.map(({ icon: Icon, title, desc, testId }, i) => (
              <motion.li
                key={title}
                variants={fadeUp}
                data-testid={testId}
                className="flex items-start gap-4 rounded-3xl border border-line bg-white/[0.04] p-6 backdrop-blur-xl transition-colors duration-300 hover:border-amber/40 md:flex-col md:items-center md:text-center"
              >
                <div className="relative shrink-0">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-amber/30 bg-night-900 text-amber shadow-glow">
                    <Icon className="h-6 w-6" />
                  </span>
                  <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber text-xs font-bold text-night-900">
                    {i + 1}
                  </span>
                </div>
                <div className="min-w-0 md:mt-4">
                  <h3 className="mb-1.5 font-serif text-xl leading-snug text-cream">{title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
                </div>
              </motion.li>
            ))}
          </motion.ol>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
